/* eslint-disable no-console */
import React, { Fragment } from 'react'
import { compose } from 'redux'
import {
  BaseComponent,
  TextFormField,
  SelectDropdownFormField,
  CheckboxFormField,
  MaskedFormField,
  RadioFormField,
  makeForm,
} from 'belle-js'
import classnames from 'classnames'
import styles from 'components/Onboarding/FormStyles.module.scss'
import { countryList, identificationTypes, yesOrNo } from 'utils/fieldOptions'
import withFormResponseHandler from 'components/Onboarding/withFormResponseHandler'
import {
  firstNameValidation,
  middleNameValidation,
  lastNameValidation,
  postalCodeValidation,
  contactBirthdateValidation,
  cityValidation,
  stateValidation,
  ssnValidation,
  phoneNumberValidation,
  dateValidation,
  trustNameValidation,
  organizationNameValidation,
  trustDateValidation,
  taxIdentificationNumberValidation,
} from 'utils/fieldValidations'
import {
  MASK_PATTERN,
  SSN_TYPE,
  MASK_CHAR_NUMBER,
  TAX_ID_NUMBER,
  DATE,
  ID_TYPE
} from 'utils/fieldMasking'
import {
  CONTACT_RECORD_TYPES,
  CONTACT_RECORD_TYPE_MAPPING,
  CONTACT_RECORD_TYPE_DEMAPPING,
  entities
} from 'utils/contactTypes'

const { ASSOCIATE } = CONTACT_RECORD_TYPES

const formStyles = classnames(styles.smallTopMarginForm, styles.Form)

class ContactForm extends BaseComponent {

  constructor (props) {
    super(props)

    const { contactType } = props

    this.state = {
      homeSameAsMobile: true,
      wasSsnEdited: false,
      wasTaxIDEdited: false,
      grantorDeceased: false,
      showIdentificationFields: false,
      defaultCountry: 'United States',
      contactLabel: contactType,
    }

    this.bind(
      'handleToggleHomePhone',
      'handleBlur',
      'handleFocus',
      'showField',
      'showRecordTypeOptions',
      'showFieldGroup',
      'renderTypeField',
      'renderRelationshipField',
      'renderButtons',
      'renderPersonForm',
      'renderEntityForm',
      'renderTypeFieldForCreation',
      'renderTypeFieldForEditing',
      'renderFormWithRecordTypeSelection',
      'handleGrantorDeceased',
      'handleCountryChange',
      'isRequired',
    )
  }

  componentWillMount () {
    const { formData, initialData, isAdd, isSuccessorDonor } = this.props
    const { defaultCountry } = this.state

    if (isSuccessorDonor) {
      this.setState({ contactLabel: 'successor donor' })
    }

    if (formData) {
      const {
        homePhone,
        mobilePhone,
        deceased
      } = formData

      const {
        primaryCitizenship,
        countryOfResidence,
      } = isAdd ? initialData : formData

      let homeSameAsMobile = false
      let showIdentificationFields = false

      if (homePhone === mobilePhone) {
        homeSameAsMobile = true
      }

      if (primaryCitizenship !== defaultCountry ||
          countryOfResidence !== defaultCountry) {
        showIdentificationFields = true
      }

      this.handleGrantorDeceased(deceased)
      this.setState({ homeSameAsMobile, showIdentificationFields })
    }
  }

  handleToggleHomePhone (homeSameAsMobile) {
    this.setState({ homeSameAsMobile })
  }

  handleFocus (type) {
    let newState = {}
    if (type === SSN_TYPE) {
      newState = {
        wasSsnEdited: true
      }
    } else  if (type === ID_TYPE) {
      newState = {
        wasTaxIDEdited: true
      }
    }
    this.setState(newState)
  }

  handleBlur (type) {
    const { formData } = this.props
    let newState = {}

    if (type === SSN_TYPE && formData.ssn === '') {
      newState = { wasSsnEdited: false }
    } else if (type === TAX_ID_NUMBER && formData.taxId === '') {
      newState = {
        wasTaxIDEdited: false
      }
    }
    this.setState(newState)
  }

  getMaskedValue (value) {
    return value !== null ? value.padStart(value.length + MASK_CHAR_NUMBER, '*') : ''
  }

  showField (name, recordType = 'Associate') {
    const { requiredFields } = this.props
    return requiredFields[recordType].includes(name)
  }

  isRequired (lookup, defaultValue) {
    const { overwroteRequiredFields = [] } = this.props
    const { length } = overwroteRequiredFields

    for (let index = 0; index < length; index += 1) {
      const [name, overwrite] = overwroteRequiredFields[index]
      if (name === lookup) return overwrite
    }

    return defaultValue
  }

  showFieldGroup (regex, recordType = 'Associate') {
    const { requiredFields } = this.props
    return requiredFields[recordType].some(name => regex.test(name))
  }

  showRecordTypeOptions () {
    const { requiredFields } = this.props
    const fieldTypeKeys = Object.keys(requiredFields)

    return fieldTypeKeys.some(k => entities.includes(k))
  }

  renderTypeFieldForCreation () {
    const {
      initialData,
      contactRecordTypes,
      allowedContactRecordTypes,
    } = this.props
    const { contactLabel } = this.state
    const typeOptions = contactRecordTypes
      .filter(type => allowedContactRecordTypes.includes(type.name))
      .map(t => ({ label: t.name, value: t.id }))

    const associateOption = typeOptions.find(o => o.label === ASSOCIATE)
    associateOption.label = CONTACT_RECORD_TYPE_MAPPING.Associate

    return (
      <div className={styles.mediumFormSection} data-test-id="contactRecordType">
        <p className={styles.heading}>Type of {contactLabel}</p>
        <SelectDropdownFormField
          name="recordTypeId"
          placeholder="Select type"
          label="recordTypeId"
          className="dropdown"
          options={typeOptions}
          value={initialData.recordType}
          required={true} />
      </div>
    )
  }

  renderTypeFieldForEditing () {
    const {
      initialData,
    } = this.props
    const { contactLabel } = this.state
    const articles = {
      Trust: 'a',
      Organization: 'an',
      Individual: 'an'
    }

    const { recordType } = initialData
    const mappedRecordType = CONTACT_RECORD_TYPE_MAPPING[recordType]
    const article = articles[mappedRecordType]
    const editText = `This contact has been created as ${article} “${mappedRecordType}”.
    To list this contact as a different contact type, create a new contact.`

    return (
      <div className={styles.mediumFormSection} data-test-id="contactRecordType">
        <p className={styles.tightHeading}>Type of {contactLabel}</p>
        <TextFormField
          name="readOnlyRecordType"
          label="contactRecordType"
          className={styles.selectedText}
          value={mappedRecordType}
          readOnly={true}
          required={true} />
        <div className={styles.smallCallout}>{editText}</div>
      </div>
    )
  }

  renderTypeField () {
    const { isAdd } = this.props

    if (isAdd) {
      return this.renderTypeFieldForCreation()
    } else {
      return this.renderTypeFieldForEditing()
    }
  }

  renderRelationshipField () {
    const {
      formData,
      contactReciprocalRoles,
      initialData,
    } = this.props

    const hideRestOfForm = this.showField('relationship') && !formData.relationship
    const relationshipOptions = contactReciprocalRoles.map(r => ({ label: r.name, value: r.id }))

    return (
      <div className={hideRestOfForm ? styles.formSection : styles.mediumFormSection} data-test-id="contactRelationship">
        <p className={styles.heading}>Relationship</p>
        <SelectDropdownFormField
          name="relationship"
          placeholder="Select relationship"
          label="relationship"
          className="dropdown"
          options={relationshipOptions}
          value={initialData.relationship || ''}
          required={true} />
      </div>
    )
  }

  renderButtons () {
    const {
      formValid,
      onClick,
      goNextSection,
      isMobile
    } = this.props
    const primaryButtonClasses = classnames(
      'button',
      'button--primary',
      !formValid ? 'is-disabled' : null,
      styles.actionButton
    )
    const cancelBtnClass = classnames(
      'button',
      'button--secondary',
      'button--form',
      styles.actionButton,
      styles.leftButton
    )
    const buttonGroup = classnames(styles.row, styles.actionButtonGroup)
    /* eslint-disable react/button-has-type */
    const cancelButton = (
      <button
        className={cancelBtnClass}
        type="button"
        onClick={goNextSection} >
        Cancel
      </button>
    )

    const saveButton = (
      <button
        className={primaryButtonClasses}
        type={formValid ? 'submit' : 'button'}
        onClick={onClick}>
        Save and Continue
      </button>
    )

    return (
      <div className={buttonGroup}>
        {!isMobile && cancelButton}
        {saveButton}
        {isMobile && cancelButton}
      </div>
    )
  }

  // Grantors
  handleGrantorDeceased (val) {
    this.setState({ grantorDeceased: val })
  }

  handleCountryChange (val) {
    const { defaultCountry } = this.state
    let showIdentificationFields = false

    if (val !== defaultCountry) {
      showIdentificationFields = true
    }

    this.setState({ showIdentificationFields })
  }

  renderGrantorDeceased (data) {
    return (
      <div className={styles.grantorDeceasedWrapper}>
        <p className={styles.grantorDeceasedTitle}>
          We’re sorry to hear about your loss.
        </p>
        <div className={styles.smallFormSection}>
          <div className={styles.formInput}>
            <p className={styles.heading}>DATE OF DEATH</p>
            <MaskedFormField
              name="dateOfDeath"
              label="dateOfDeath"
              className={styles.FormFieldInSection}
              placeholder="MM/DD/YYYY"
              mask={DATE}
              validations={dateValidation.rules}
              validationErrors={dateValidation.messages}
              value={data.dateOfDeath || ''}
              testId="dateOfDeath" />
          </div>
        </div>
      </div>
    )
  }

  renderRegulatoryQuestions (type) {
    const { formData, contactType } = this.props
    const firstRegulatoryQuestion = `Is this person affiliated with or employed by a stock exchange or member firm of an exchange or FINRA, or a municipal securities broker-dealer?`
    const secondRegulatoryQuestion = `Is this person or a member of their household/immediate family a 10% shareholder, policy-making officer or member of the board of directors of a publicly traded company?`
    const orgRegulatoryQuestion = `Is this ${type ? type.toLowerCase() : 'organization'} a 10% shareholder, policy-making officer or member of the board of directors of a publicly traded company?`
    const questionStyles = classnames(styles.question, styles.questionPadding)
    const regulatoryHeading = classnames(styles.regulatoryHeading, styles.regulatoryHeadingMargin)
    const showFinra = this.showField('finraAffiliated', type)
    const showPublic = this.showField('publicCompanyAffiliated', type)

    if (!showFinra && !showPublic) {
      return
    }

    return (
      <Fragment>
        <p className={regulatoryHeading}>Regulatory Questions</p>
        {showFinra &&
          <div className={styles.smallFormSection}>
            <div className={styles.twoColumn}>
              <div className={styles.regulatory}>
                <p className={questionStyles}>
                  {firstRegulatoryQuestion}
                </p>
              </div>
              <div className={styles.radioBlock}>
                <RadioFormField
                  name="finraAffiliated"
                  horizontal={false}
                  options={yesOrNo}
                  label="Finra Affiliation"
                  required={false}
                  testId="employmentDetailsForm__affiliationFinraStockExBD__field"
                  value={formData.finraAffiliated || ''} />
              </div>
            </div>
          </div>
        }

        {showPublic &&
          <div className={styles.largeFormSection}>
            <div className={styles.twoColumn}>
              <div className={styles.regulatory}>
                <p className={questionStyles}>
                  {contactType === 'Grantor' ? orgRegulatoryQuestion : secondRegulatoryQuestion}
                </p>
              </div>
              <div className={styles.radioBlock}>
                <RadioFormField
                  name="publicCompanyAffiliated"
                  horizontal={false}
                  options={yesOrNo}
                  label="Public Company Affiliation"
                  required={false}
                  testId="employmentDetailsForm__director10ShPmo__field"
                  value={formData.publicCompanyAffiliated || ''} />
              </div>
            </div>
          </div>
        }
      </Fragment>
    )
  }

  renderMailingAddress () {
    const { isMobile, initialData } = this.props
    const addressStyles = classnames(
      styles.formSection,
      styles.primaryAddressMargin
    )

    return (
      <div className={addressStyles}>
        <p className={styles.heading}>Primary Address</p>
        <div className={styles.twoColumn}>
          <div>
            <TextFormField
              name="mailingStreet"
              label="mailingStreet"
              placeholder="Street name and number"
              className={styles.FormFieldInSection}
              value={initialData.mailingStreet || ''}
              maxLength={255}
              required={false} />
            <TextFormField
              name="mailingCity"
              placeholder="City"
              label="mailingCity"
              className={styles.FormFieldInSection}
              maxLength={40}
              validations={cityValidation.rules}
              validationErrors={cityValidation.messages}
              value={initialData.mailingCity || ''}
              required={false} />
            {
              isMobile &&
              <div className={styles.twoColumnAddress}>
                <TextFormField
                  name="mailingState"
                  placeholder="State"
                  className={styles.FormFieldInSection}
                  maxLength={80}
                  label="mailingState"
                  value={initialData.mailingState || ''}
                  validations={stateValidation.rules}
                  validationErrors={stateValidation.messages}
                  required={false} />
                <TextFormField
                  name="mailingPostalCode"
                  placeholder="Zipcode"
                  maxLength={20}
                  label="mailingPostalCode"
                  className={styles.FormFieldInSection}
                  validations={postalCodeValidation.rules}
                  validationErrors={postalCodeValidation.messages}
                  value={initialData.mailingPostalCode || ''}
                  required={false} />
              </div>
            }
            {
              !isMobile &&
              <TextFormField
                name="mailingPostalCode"
                placeholder="Zipcode"
                maxLength={20}
                label="mailingPostalCode"
                className={styles.FormFieldInSection}
                validations={postalCodeValidation.rules}
                validationErrors={postalCodeValidation.messages}
                value={initialData.mailingPostalCode || ''}
                required={false} />
            }
          </div>
          <div>
            {
              !isMobile &&
              <TextFormField
                name="mailingState"
                placeholder="State"
                label="mailingState"
                className={styles.FormFieldInSection}
                maxLength={80}
                value={initialData.mailingState || ''}
                validations={stateValidation.rules}
                validationErrors={stateValidation.messages}
                required={false} />
            }
            <SelectDropdownFormField
              name="mailingCountry"
              label="mailingCountry"
              options={countryList}
              value={initialData.mailingCountry || 'United States'}
              required={false} />
          </div>
        </div>
      </div>
    )
  }

  getRecordType () {
    const {
      initialData,
      contactRecordTypes,
      formData,
    } = this.props

    const currentRecordTypeId = formData.recordTypeId ?
      formData.recordTypeId : initialData.recordTypeId

    if (!currentRecordTypeId) {
      return
    }

    const type = contactRecordTypes
      .find(t => t.id === currentRecordTypeId).name

    return type
  }

  renderPersonForm () {
    const {
      formData,
      initialData,
      isProspect,
    } = this.props
    const {
      grantorDeceased,
      defaultCountry
    } = this.state

    const { lastFourDigitSsn, lastFourDigitIdentificationNumber } = initialData || {}
    const isSSNMasked = !this.state.wasSsnEdited && lastFourDigitSsn

    let homePhoneNumber = formData.homePhone

    if (this.state.homeSameAsMobile) {
      homePhoneNumber = formData.mobilePhone
    } else if (homePhoneNumber === formData.mobilePhone) {
      formData.homePhone = ''
    }
    const hideRestOfForm = this.showField('relationship') && !formData.relationship

    // Grantors
    const isIdentificationMasked = !this.state.wasIDEdited && lastFourDigitIdentificationNumber
    const citizenshipCallout = `${isProspect ? 'If you have' : 'If this person has'} citizenship in more than one country, we will help you add that`
    const type = this.getRecordType()

    const {
      primaryCitizenship,
      countryOfResidence,
    } = formData
    let showIdentificationFields = false

    if (primaryCitizenship !== defaultCountry ||
       countryOfResidence !== defaultCountry) {
      showIdentificationFields = true
    }

    const twoColumnFormSection = classnames(
      styles.twoColumn,
      styles.formSection,
      styles.citizenship
    )

    if (hideRestOfForm) {
      return (
        <Fragment>
          {this.showField('relationship') && this.renderRelationshipField()}
        </Fragment>
      )
    } else {
      return (
        <Fragment>
          {this.showField('relationship') && this.renderRelationshipField()}
          <div className={styles.formSection}>
            <p className={styles.heading}>Full Name</p>
            <TextFormField
              name="firstName"
              label="firstName"
              className={styles.FormFieldInSection}
              placeholder="First name"
              validations={firstNameValidation.rules}
              validationErrors={firstNameValidation.messages}
              maxLength={35}
              value={initialData.firstName || ''}
              required={true} />

            <TextFormField
              name="middleName"
              label="middleName"
              className={styles.FormFieldInSection}
              placeholder="Middle name"
              validations={middleNameValidation.rules}
              validationErrors={middleNameValidation.messages}
              maxLength={35}
              value={initialData.middleName || ''}
              required={false} />

            <TextFormField
              name="lastName"
              label="lastName"
              className={styles.lastFormFieldInSection}
              maxLength={35}
              placeholder="Last name"
              validations={lastNameValidation.rules}
              validationErrors={lastNameValidation.messages}
              value={initialData.lastName || ''}
              required={true} />
          </div>

          {this.showField('birthdate') &&
          <div className={styles.formSection}>
            <p className={styles.heading}>Date of Birth</p>

            <MaskedFormField
              name="birthdate"
              label="Date of Birth"
              value={initialData.birthdate}
              validations={contactBirthdateValidation.rules}
              className={styles.FormField}
              validationErrors={contactBirthdateValidation.messages}
              placeholder="MM/DD/YYYY"
              mask="date"
              required={this.isRequired('birthdate', false)} />

            {this.showField('grantorDeceased') &&
              <div>
                <div>
                  <CheckboxFormField
                    className={styles.formToggle}
                    name="deceased"
                    label="THIS GRANTOR IS DECEASED"
                    testId="deceased"
                    onChange={event => this.handleGrantorDeceased(event)}
                    value={initialData.deceased} />
                </div>
              </div>
            }
          </div>}

          {this.showField('grantorDeceased', type) && grantorDeceased && this.renderGrantorDeceased(initialData)}

          {!grantorDeceased &&
            <Fragment>
              {this.showField('ssn', type) &&
              <div className={styles.formSection}>
                <p className={styles.heading}>Social Security Number</p>
                <MaskedFormField
                  name="ssn"
                  label="Social Security Number"
                  placeholder="123-45-6789"
                  className={styles.FormField}
                  mask={isSSNMasked ? MASK_PATTERN : 'ssn'}
                  validations={isSSNMasked ? undefined : ssnValidation.rules}
                  validationErrors={isSSNMasked ? undefined : ssnValidation.messages}
                  value={isSSNMasked ? lastFourDigitSsn : ''}
                  onBlur={isSSNMasked ?   undefined : this.handleBlur.bind(this, SSN_TYPE)}
                  onFocus={isSSNMasked ? this.handleFocus.bind(this, SSN_TYPE) :  undefined}
                  required={false} />
              </div>}

              {this.showFieldGroup(/mailing/) &&
                this.renderMailingAddress()
              }

              {this.showFieldGroup(/Phone/) &&
              <div className={styles.twoColumn}>
                <div className={styles.formSection}>
                  <p className={styles.heading}>Mobile Phone Number</p>
                  <MaskedFormField
                    type="tel"
                    className={styles.FormField}
                    name="mobilePhone"
                    label="Mobile Phone Number"
                    placeholder="555-666-7777"
                    mask="phoneNumber"
                    minLength={10}
                    required={false}
                    validations={phoneNumberValidation.rules}
                    validationErrors={phoneNumberValidation.messages}
                    value={initialData.mobilePhone || ''} />
                </div>
                {this.showField('homePhone') &&
                <div className={styles.formSection}>
                  <p className={styles.heading}>Home Phone Number</p>
                  <div className="toggle">
                    <MaskedFormField
                      type="tel"
                      name="homePhone"
                      label="Home Phone Number"
                      className={styles.FormField}
                      placeholder="555-666-7777"
                      mask="phoneNumber"
                      required={false}
                      validations={phoneNumberValidation.rules}
                      validationErrors={phoneNumberValidation.messages}
                      disabled={this.state.homeSameAsMobile}
                      value={homePhoneNumber} />
                  </div>
                  <CheckboxFormField
                    name="homeSameAsMobile"
                    className={styles.formToggle}
                    label="Home phone is the same as mobile phone"
                    testId="personalInformationForm__homeSameAsMobile__field"
                    value={this.state.homeSameAsMobile}
                    onChange={this.handleToggleHomePhone} />
                </div>
                }
              </div>}

              {this.showFieldGroup(/country/, type) &&
                <Fragment>
                  <div className={twoColumnFormSection}>
                    <div>
                      <p className={styles.heading}>Country of Citizenship</p>
                      <SelectDropdownFormField
                        name="primaryCitizenship"
                        label="Country of Citizenship"
                        className={styles.FormField}
                        options={countryList}
                        onChange={this.handleCountryChange}
                        value={formData.primaryCitizenship || defaultCountry}
                        testId="primaryCitizenship"
                        required={false} />
                    </div>
                    <div className={styles.callout}>
                      {citizenshipCallout}
                    </div>
                  </div>
                  <div className={styles.formSection}>
                    <p className={styles.heading}>Country of Legal Residence</p>
                    <SelectDropdownFormField
                      name="countryOfResidence"
                      label="Country of Residence"
                      className={styles.FormField}
                      options={countryList}
                      onChange={this.handleCountryChange}
                      value={formData.countryOfResidence || defaultCountry}
                      testId="countryOfResidence"
                      required={false} />
                  </div>
                  {showIdentificationFields &&
                  <Fragment>
                    <div className={styles.formSection}>
                      <p className={styles.heading}>Country of Birth</p>
                      <SelectDropdownFormField
                        name="countryOfBirth"
                        label="Country of Residence"
                        className={styles.FormField}
                        options={countryList}
                        value={formData.countryOfBirth || defaultCountry}
                        testId="countryOfBirth"
                        required={false} />
                    </div>
                    <div className={styles.twoColumn}>
                      <div className={styles.formSection}>
                        <p className={styles.heading}>Identification Type</p>
                        <SelectDropdownFormField
                          name="identificationType"
                          label="Identification Type"
                          placeholder="Select Identification Type"
                          className={styles.FormField}
                          options={identificationTypes}
                          value={formData.identificationType}
                          testId="identificationType"
                          required={false} />
                      </div>
                      <div className={styles.formSection}>
                        <p className={styles.heading}>Identification Number</p>
                        <TextFormField
                          name="identificationNumber"
                          label="Identification Number"
                          placeholder="123-45-6789"
                          className={styles.FormField}
                          maxLength={32}
                          validations={isIdentificationMasked ?
                            undefined : { isValidIdentificationNumber: true }}
                          validationErrors={
                            isIdentificationMasked ? undefined : { isValidIdentificationNumber: 'This field may contain only letters, numbers, and spaces.' }
                          }
                          value={
                            isIdentificationMasked ? this.getMaskedValue(lastFourDigitIdentificationNumber) : ''
                          }
                          testId="identityVerificationForm__identificationNumber__field"
                          onBlur={isIdentificationMasked ?  undefined : this.handleBlur.bind(this, ID_TYPE)} // eslint-disable-line
                          onFocus={isIdentificationMasked ? this.handleFocus.bind(this, ID_TYPE) : undefined} // eslint-disable-line
                          required={false} />
                      </div>
                    </div>

                    <div className={styles.formSection}>
                      <p className={styles.heading}>State or Country of Issuance</p>
                      <TextFormField
                        name="localeOfIdIssuance"
                        label="localeOfIdIssuance"
                        className={styles.FormField}
                        maxLength={50}
                        placeholder="State or Country of Issuance"
                        validations={{ isValidEntityName: true }}
                        validationErrors={{ isValidEntityName: 'Hmm, that’s not a valid state or country.' }}
                        value={formData.localeOfIdIssuance || ''}
                        testId="identityVerificationForm__localeOfIdIssuance__field"
                        required={false} />
                    </div>

                    <div className={styles.twoColumn}>
                      <div className={styles.formSection}>
                        <p className={styles.heading}>Issue Date</p>
                        <MaskedFormField
                          name="identificationIssueDate"
                          label="Identification Issue Date"
                          value={formData.identificationIssueDate}
                          className={styles.FormField}
                          mask="date"
                          placeholder="MM/DD/YYYY"
                          validations={{ isValidStartingDate: true, isValidFutureDate: true }}
                          validationErrors={{
                            isValidStartingDate: 'Hmm, that’s not a valid date.',
                            isValidFutureDate: 'That date is too far in the future.'
                          }}
                          validationError="Please enter a valid date."
                          testId="identityVerificationForm__identificationIssueDate__field"
                          required={false} />
                      </div>
                      <div className={styles.formSection}>
                        <p className={styles.heading}>Expiration Date</p>
                        <MaskedFormField
                          name="identificationExpirationDate"
                          label="Identification Expiration Date"
                          value={formData.identificationExpirationDate}
                          className={styles.FormField}
                          mask="date"
                          placeholder="MM/DD/YYYY"
                          validations={{
                            isValidExpirationDate: true,
                            isValidFutureDate: true
                          }}
                          validationErrors={{
                            isValidExpirationDate: 'Hmm, that’s not a valid date.',
                            isValidFutureDate: 'That date is too far in the future.'
                          }}
                          testId="identityVerificationForm__identificationExpirationDate__field"
                          required={false} />
                      </div>
                    </div>
                  </Fragment>
                  }
                </Fragment>
              }

              {this.renderRegulatoryQuestions(type)}

              {this.showField('email') &&
              <div className={styles.formSection} >
                <div className={styles.twoColum}>
                  <div>
                    <p className={styles.heading}>Email Address</p>
                    <TextFormField
                      name="email"
                      className={styles.FormFieldInSection}
                      maxLength={80}
                      label=""
                      placeholder="name@email.com"
                      value={initialData.email || ''}
                      validations="isEmail"
                      validationErrors={{
                        isEmail: 'Hmm, that’s not a valid email address.'
                      }}
                      required={this.isRequired('email', false)} />
                  </div>
                </div>
              </div>}
            </Fragment>
          }
        </Fragment>
      )
    }
  }

  renderEntityForm () {
    const {
      initialData,
      formData,
    } = this.props

    const { defaultCountry } = this.state
    const { lastFourDigitTaxId } = initialData || {}
    const isTaxIDMasked = !this.state.wasTaxIDEdited && lastFourDigitTaxId
    const nameDefaultText = 'Entity Name'
    const type = this.getRecordType()
    const headingStylesConfig = {}

    let disableStateOfIncorporation = false

    if (formData.countryOfIncorporation !== defaultCountry) {
      disableStateOfIncorporation = true
    }

    headingStylesConfig[styles.headingDisabled] = disableStateOfIncorporation

    const headingStyles = classnames(
      styles.heading,
      headingStylesConfig
    )

    const stateOfIncorporation = disableStateOfIncorporation ? '' :
      initialData.stateOfIncorporation

    return (
      <Fragment>
        {this.showField('organizationName', type) &&
        <div className={styles.formSection} >
          <p className={styles.heading}>{nameDefaultText}</p>
          <TextFormField
            name="organizationName"
            className={styles.FormField}
            maxLength={80}
            label=""
            placeholder={nameDefaultText}
            validations={organizationNameValidation.rules}
            validationErrors={organizationNameValidation.messages}
            value={initialData.lastName || ''}
            required={true} />
        </div>}

        {this.showField('trustName', type) &&
        <div className={styles.formSection} >
          <p className={styles.heading}>{nameDefaultText}</p>
          <TextFormField
            name="trustName"
            className={styles.FormField}
            maxLength={80}
            label=""
            placeholder={nameDefaultText}
            validations={trustNameValidation.rules}
            validationErrors={trustNameValidation.messages}
            value={initialData.lastName || ''}
            required={true} />
        </div>}

        {this.showFieldGroup(/mailing/, type) &&
          this.renderMailingAddress()
        }

        {this.showField('dateOfTrust', type) &&
        <div className={styles.formSection}>
          <p className={styles.heading}>Date of Trust</p>
          <MaskedFormField
            name="dateOfTrust"
            label="Date of Birth"
            validations={trustDateValidation.rules}
            validationErrors={trustDateValidation.messages}
            value={initialData.dateOfTrust}
            className={styles.FormField}
            placeholder="MM/DD/YYYY"
            mask="date" />
        </div>}

        {this.showField('taxIdentificationNumber', type) &&
        <div className={styles.formSection} >
          <p className={styles.heading}>Tax Id Number (TIN)</p>
          <TextFormField
            name="taxIdentificationNumber"
            className={styles.FormField}
            label=""
            placeholder="12-3456789"
            validations={isTaxIDMasked ?
              undefined : taxIdentificationNumberValidation.rules}
            validationErrors={
              isTaxIDMasked ? undefined : taxIdentificationNumberValidation.messages}
            value={isTaxIDMasked ? this.getMaskedValue(lastFourDigitTaxId) : ''}
            onBlur={isTaxIDMasked ?  undefined : this.handleBlur.bind(this, TAX_ID_NUMBER)} // eslint-disable-line
            onFocus={isTaxIDMasked ? this.handleFocus.bind(this, TAX_ID_NUMBER) : undefined} // eslint-disable-line
            maxLength={32} />
        </div>}

        {this.showField('countryOfIncorporation', type) &&
        <div className={styles.formSection}>
          <p className={styles.heading}>COUNTRY OF INCORPORATION/ESTABLISHMENT</p>
          <SelectDropdownFormField
            name="countryOfIncorporation"
            label="countryOfIncorporation"
            placeholder="State"
            className={styles.FormField}
            options={countryList}
            value={initialData.countryOfIncorporation || defaultCountry}
            testId="countryOfIncorporation"
            required={false} />
        </div>}

        {this.showField('stateOfIncorporation', type) &&
        <div className={styles.formSection}>
          <p className={headingStyles}>STATE OF INCORPORATION/ESTABLISHMENT</p>
          <TextFormField
            disabled={disableStateOfIncorporation}
            name="stateOfIncorporation"
            label="stateOfIncorporation"
            placeholder="State"
            className={styles.FormField}
            maxLength={50}
            validations={{ isValidEntityName: true }}
            validationErrors={{ isValidEntityName: 'Hmm, that’s not a valid state or country.' }}
            value={stateOfIncorporation || ''}
            testId="stateOfIncorporation"
            required={false} />
        </div>
        }

        {this.renderRegulatoryQuestions(type)}
      </Fragment>
    )
  }

  renderFormWithRecordTypeSelection () {
    const { formData, contactRecordTypes, allowedContactRecordTypes } = this.props
    const { readOnlyRecordType, recordTypeId } = formData
    const unMappedRecordType = CONTACT_RECORD_TYPE_DEMAPPING[readOnlyRecordType]
    const hideRestOfForm = !recordTypeId && !unMappedRecordType
    const recordTypes = contactRecordTypes.filter(t => allowedContactRecordTypes.includes(t.name))
    const recordTypeSelected = recordTypes
      .find(t => (t.id === recordTypeId) || (t.name === unMappedRecordType))

    const { name } = recordTypeSelected || {}
    const renderForm = name === ASSOCIATE ? this.renderPersonForm : this.renderEntityForm

    return (
      <div className={formStyles}>
        {this.renderTypeField()}
        {!hideRestOfForm && renderForm()}
        {this.renderButtons()}
      </div>
    )
  }

  render () {
    if (this.showRecordTypeOptions()) {
      return this.renderFormWithRecordTypeSelection()
    } else {
      return (
        <div className={formStyles}>
          {this.renderPersonForm()}
          {this.renderButtons()}
        </div>
      )
    }
  }

}

export default compose(
  makeForm,
  withFormResponseHandler,
)(ContactForm)



// WEBPACK FOOTER //
// ./src/components/Onboarding/Accounts/ContactForm.jsx